/** @jsxImportSource @emotion/react */

import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import IconButton from '@mui/material/IconButton'
import { Link } from 'react-router-dom'
import Divider from '@mui/material/Divider'
import { FC } from 'react'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { Loader } from 'components/Loader'
import { FIND_ALL_DENTAL_SCHOOL_LOCATIONS } from 'queries/dentalSchools'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import * as tableButtonsClasses from 'components/TableButtons/styles'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import {
  findAllDentalSchoolLocations,
  findAllDentalSchoolLocationsVariables,
} from '__generatedTypes__/findAllDentalSchoolLocations'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import TabsNavigation from 'components/TabsNavigation'
import FAQ, { FAQ_TAB } from 'pagesNew/FAQ'
import { FaqPages } from '__generatedTypes__/globalTypes'
import { SectionItem } from '../SuccessStories/SectionItem'
import * as commonClasses from '../SuccessStories/styles'
import * as classes from '../StudyGuide/styles'

type Props = {
  platform: Platform
  isOptometry: boolean
}

export const DentalSchoolStatistics: FC<Omit<Props, 'platform'>> = withPlatformCheck(
  ({ platform, isOptometry }: Props) => {
    const { getLink } = useLink()

    const { data, loading } = useQuery<findAllDentalSchoolLocations, findAllDentalSchoolLocationsVariables>(
      FIND_ALL_DENTAL_SCHOOL_LOCATIONS,
      {
        fetchPolicy: 'network-only',
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              platform: {
                id: platform.id,
              },
              isOptometry,
            },
          },
        },
      }
    )

    const dentalSchoolSections = data?.findAllDentalSchoolLocation?.items || []

    if (loading) {
      return <Loader />
    }

    return (
      <div>
        {dentalSchoolSections.length ? (
          <ul css={commonClasses.list}>
            {dentalSchoolSections.map((section) => {
              const sectionItems = section.dentalSchool.map(({ id, title }) => ({ id, label: title }))
              return (
                <li key={section.id} css={commonClasses.listItem} data-testid={`${section.id}-school`}>
                  <div>
                    <Typography variant="subtitle2" css={commonClasses.title}>
                      {section.title}
                    </Typography>
                    <SectionItem orderIds={section.dentalSchoolIds} items={sectionItems} />
                  </div>
                  <Link
                    to={getLink(
                      isOptometry
                        ? cmsRoutes.ORGANIZE_EDIT_OPTOMETRY_SCHOOL_STATISTICS
                        : cmsRoutes.ORGANIZE_EDIT_DENTAL_SCHOOL_STATISTICS,
                      { id: section.id }
                    )}
                  >
                    <IconButton css={commonClasses.editButton} LinkComponent={Link} data-testid="edit-button">
                      <EditIcon />
                    </IconButton>
                  </Link>
                </li>
              )
            })}
          </ul>
        ) : (
          <Typography variant="h6">No sections found</Typography>
        )}

        <Link
          css={commonClasses.addLink}
          to={getLink(
            isOptometry
              ? cmsRoutes.ORGANIZE_CREATE_OPTOMETRY_SCHOOL_STATISTICS
              : cmsRoutes.ORGANIZE_CREATE_DENTAL_SCHOOL_STATISTICS
          )}
        >
          <Button css={commonClasses.addButton} variant="outlined" color="blue" size="large" fullWidth>
            Add new section in {isOptometry ? 'Optometry' : 'Dental'} School Statistics{' '}
            <AddRoundedIcon css={tableButtonsClasses.iconPlus} />
          </Button>
        </Link>

        <Divider css={classes.divider} />

        <div>
          <TabsNavigation tabs={[FAQ_TAB]} value={FAQ_TAB} css={classes.tabs} />

          <FAQ page={isOptometry ? FaqPages.OptometrySchool : FaqPages.DentalSchool} />
        </div>
      </div>
    )
  }
)
